import React from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { motion } from 'framer-motion';
import { ChevronDown, Camera, Play, FileText } from 'lucide-react';

export const HeroSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 bg-vilarias-black" />
      <div className="absolute inset-0 bg-hero-glow opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-vilarias-black/50 to-vilarias-black" />

      {/* Animated grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(0,141,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,141,255,0.3) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      {/* Floating orbs */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-vilarias-accent/10 blur-[100px]"
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full bg-vilarias-glow/5 blur-[120px]"
        animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 text-center section-padding max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl tracking-wider text-white mb-6">
            {t.hero.headline}
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="mb-10"
        >
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-vilarias-accent to-transparent mx-auto mb-6" />
          <p className="text-lg md:text-xl text-vilarias-text-secondary tracking-[0.2em] uppercase">
            {t.hero.tagline}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <a
            href="#portfolio"
            onClick={(e) => { e.preventDefault(); document.querySelector('#portfolio')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="group flex items-center gap-2 px-8 py-3 bg-vilarias-accent text-white rounded-full btn-glow text-sm tracking-wide uppercase font-medium"
          >
            <Camera size={16} />
            {t.hero.ctaPortfolio}
          </a>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="group flex items-center gap-2 px-8 py-3 border border-vilarias-border text-white rounded-full hover:border-vilarias-accent hover:text-vilarias-glow transition-all text-sm tracking-wide uppercase font-medium"
          >
            <Play size={16} />
            {t.hero.ctaBook}
          </a>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="group flex items-center gap-2 px-8 py-3 border border-vilarias-border text-white rounded-full hover:border-vilarias-accent hover:text-vilarias-glow transition-all text-sm tracking-wide uppercase font-medium"
          >
            <FileText size={16} />
            {t.hero.ctaQuote}
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
      >
        <span className="text-xs text-vilarias-text-secondary tracking-widest uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown size={20} className="text-vilarias-accent" />
        </motion.div>
      </motion.div>
    </section>
  );
};
